/**
 * Browser-side wallet key derivation and transaction signing.
 * Private keys are derived here in the browser, never sent to the server.
 * The server provides unsigned transaction data; the browser signs and returns the hex.
 */

import { mnemonicToSeedSync } from '@scure/bip39';
import { HDKey }              from '@scure/bip32';
import * as btc               from '@scure/btc-signer';
import { hex as hexUtil }     from '@scure/base';
import { ethers }             from 'ethers';
import { TronWeb }            from 'tronweb';

/* ── HD paths (must match backend/utils/walletDerivation.js) ── */
const PATHS = {
  btc: "m/84'/0'/0'/0/0",   // BIP84 native segwit
  eth: "m/44'/60'/0'/0/0",
  trx: "m/44'/195'/0'/0/0",
};

/* ── Key derivation ─────────────────────────────────────────── */

function getRoot(mnemonic) {
  const seed = mnemonicToSeedSync(mnemonic.trim().toLowerCase());
  return HDKey.fromMasterSeed(seed);
}

function deriveBtcNode(mnemonic) {
  return getRoot(mnemonic).derive(PATHS.btc);
}

function deriveEthNode(mnemonic) {
  return getRoot(mnemonic).derive(PATHS.eth);
}

function deriveTrxNode(mnemonic) {
  return getRoot(mnemonic).derive(PATHS.trx);
}

/* ── Address derivation (used for forgot-password ownership proof) ── */

export function deriveAddressesFromMnemonic(mnemonic) {
  const root = getRoot(mnemonic);

  // BTC: P2WPKH (bc1q…)
  const btcNode  = root.derive(PATHS.btc);
  const btcPub   = btcNode.publicKey;
  const btcAddr  = btc.p2wpkh(btcPub, btc.NETWORK).address;

  // ETH
  const ethNode  = root.derive(PATHS.eth);
  const ethPriv  = '0x' + hexUtil.encode(ethNode.privateKey);
  const ethAddr  = new ethers.Wallet(ethPriv).address;

  // TRX
  const trxNode  = root.derive(PATHS.trx);
  const trxPriv  = hexUtil.encode(trxNode.privateKey);
  const tw       = new TronWeb({ fullHost: 'https://api.trongrid.io' });
  const trxAddr  = tw.address.fromPrivateKey(trxPriv);

  return { btc: btcAddr, eth: ethAddr, trx: trxAddr };
}

/* ── BTC signing (uses @scure/btc-signer, no Buffer needed) ─── */

/* Converts a bech32/legacy BTC address to its output script Uint8Array */
function addrToScript(address) {
  return btc.OutScript.encode(btc.Address(btc.NETWORK).decode(address));
}

export function signBtcTransaction({ mnemonic, utxos, toAddress, amountSats, platformFeeAddress, platformFeeSats, feeRateSatVbyte, fromAddress, subtractFee = false }) {
  const node    = deriveBtcNode(mnemonic);
  const privKey = node.privateKey;
  const pubKey  = node.publicKey;
  const p2wpkh  = btc.p2wpkh(pubKey, btc.NETWORK);

  const tx = new btc.Transaction();

  let totalIn = BigInt(0);
  const selected = [];

  if (subtractFee) {
    // Collect UTXOs to cover amountSats only — fees come out of the amount
    for (const utxo of utxos) {
      selected.push(utxo);
      tx.addInput({ txid: utxo.txid, index: utxo.vout, witnessUtxo: { script: p2wpkh.script, amount: BigInt(utxo.value) } });
      totalIn += BigInt(utxo.value);
      if (totalIn >= BigInt(amountSats)) break;
    }
    const change     = totalIn - BigInt(amountSats);
    const numOutputs = change > BigInt(546) ? 3 : 2;
    const finalSize  = selected.length * 68 + numOutputs * 31 + 10;
    const minerFee   = BigInt(Math.ceil(finalSize * feeRateSatVbyte));
    let recipientSats = BigInt(amountSats) - minerFee - BigInt(platformFeeSats);
    if (change <= BigInt(546)) recipientSats += change;
    if (recipientSats < BigInt(546)) throw new Error('Amount too small to cover fees after deduction');

    tx.addOutput({ script: addrToScript(toAddress),          amount: recipientSats         });
    tx.addOutput({ script: addrToScript(platformFeeAddress),  amount: BigInt(platformFeeSats) });
    if (change > BigInt(546)) {
      tx.addOutput({ script: p2wpkh.script, amount: change });
    }
  } else {
    // Greedily add UTXOs until we have enough to cover amount + fees
    for (const utxo of utxos) {
      selected.push(utxo);
      tx.addInput({
        txid: utxo.txid,
        index: utxo.vout,
        witnessUtxo: { script: p2wpkh.script, amount: BigInt(utxo.value) },
      });
      totalIn += BigInt(utxo.value);

      const estSize  = selected.length * 68 + 3 * 31 + 10;
      const minerFee = BigInt(Math.ceil(estSize * feeRateSatVbyte));
      if (totalIn >= BigInt(amountSats) + BigInt(platformFeeSats) + minerFee) break;
    }

    const finalSize  = selected.length * 68 + 3 * 31 + 10;
    const minerFee   = BigInt(Math.ceil(finalSize * feeRateSatVbyte));
    const change     = totalIn - BigInt(amountSats) - BigInt(platformFeeSats) - minerFee;

    if (change < BigInt(0)) throw new Error('Insufficient BTC balance to cover miner fee');

    tx.addOutput({ script: addrToScript(toAddress),          amount: BigInt(amountSats)      });
    tx.addOutput({ script: addrToScript(platformFeeAddress), amount: BigInt(platformFeeSats) });
    if (change > BigInt(546)) {
      tx.addOutput({ script: p2wpkh.script, amount: change });
    }
  }

  // Sign each input individually (v2 requirement for P2WPKH)
  for (let i = 0; i < selected.length; i++) {
    tx.signIdx(privKey, i);
    tx.finalizeIdx(i);
  }
  return tx.hex;
}

/* ── ETH native signing ──────────────────────────────────────── */

export async function signEthTransaction({ mnemonic, unsignedTx }) {
  const node       = deriveEthNode(mnemonic);
  const privateKey = '0x' + hexUtil.encode(node.privateKey);
  const wallet     = new ethers.Wallet(privateKey);
  return wallet.signTransaction(unsignedTx);
}

/* ── USDT ERC20 signing ──────────────────────────────────────── */

export async function signErc20Transaction({ mnemonic, unsignedTx }) {
  const node       = deriveEthNode(mnemonic);
  const privateKey = '0x' + hexUtil.encode(node.privateKey);
  const wallet     = new ethers.Wallet(privateKey);
  return wallet.signTransaction(unsignedTx);
}

/* ── TRX native signing ──────────────────────────────────────── */

export async function signTrxTransaction({ mnemonic, unsignedTx }) {
  const node       = deriveTrxNode(mnemonic);
  const privateKey = hexUtil.encode(node.privateKey);
  // TronWeb.trx.sign is a local crypto op — no network call
  const tw = new TronWeb({ fullHost: 'https://api.trongrid.io' });
  return tw.trx.sign(unsignedTx, privateKey);
}

/* ── USDT TRC20 signing (sign two txs: recipient + fee) ──────── */

export async function signTrc20Transactions({ mnemonic, unsignedTxs }) {
  const node       = deriveTrxNode(mnemonic);
  const privateKey = hexUtil.encode(node.privateKey);
  const tw = new TronWeb({ fullHost: 'https://api.trongrid.io' });
  // unsignedTxs = [recipientTx, feeTx]
  return Promise.all(unsignedTxs.map(u => tw.trx.sign(u, privateKey)));
}
